import axios from "axios";
import { captureException } from "@/lib/sentry";
import { useAuthStore } from "@/stores/useAuthStore";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "http://localhost:8000/api";

// Instance Axios globale
export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Interceptor to add the token to the request headers
api.interceptors.request.use(
  (config) => {
    const token = useAuthStore.getState().token;
    if (token) {
      config.headers.Authorization = `JWT ${token}`;
    }
    return config;
  },
  (error) => {
    captureException(error);
    return Promise.reject(error);
  },
);

let isRefreshing = false;
let failedQueue: {
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}[] = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token!);
    }
  });

  failedQueue = [];
};

// Interceptor to globally handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Capture error with Sentry
    captureException(error);

    // Handle 401 errors (unauthenticated)
    if (error.response?.status === 401 && !originalRequest._retry) {
      const data = error.response.data as {
        code?: string;
        messages?: { message: string }[];
      };
      const isExpired =
        data?.code === "token_not_valid" &&
        data?.messages?.some((msg) => msg.message === "Token is expired");

      if (isExpired) {
        if (isRefreshing) {
          return new Promise(function (resolve, reject) {
            failedQueue.push({ resolve, reject });
          })
            .then((token) => {
              originalRequest.headers.Authorization = `JWT ${token}`;
              return api(originalRequest);
            })
            .catch((err) => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        const { refreshToken, setToken, clear } = useAuthStore.getState();

        if (!refreshToken) {
          clear();
          if (!window.location.pathname.includes("/login")) {
            window.location.href = "/login";
          }
          return Promise.reject(error);
        }

        try {
          const response = await axios.post(`${API_BASE_URL}/token/refresh/`, {
            refresh: refreshToken,
          });

          const { access } = response.data;
          setToken(access);

          processQueue(null, access);

          originalRequest.headers.Authorization = `JWT ${access}`;
          return api(originalRequest);
        } catch (refreshError) {
          processQueue(refreshError as Error, null);
          clear();
          if (!window.location.pathname.includes("/login")) {
            window.location.href = "/login";
          }
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // If not expired but still 401, or if it's the retry that failed
      const { clear } = useAuthStore.getState();
      clear();

      // Only redirect if not already on login page to avoid hard refresh during login/debug
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }

    // Handle 403 errors (unauthorized)
    if (error.response?.status === 403) {
      // Redirect or display a message
    }

    return Promise.reject(error);
  },
);

export default api;
